import { useEffect, useState, useRef } from 'react'
import { sendGet } from '../../hooks/use-fetch'
import { toast } from 'react-toastify'
import DataTable from '../../Components/Table/DataTable'
import EllipsisIcon from '@mui/icons-material/MoreHoriz'
import { usePagination, type PaginationProps } from '../../hooks/use-pagination'
import { type RowData, Column, ColumnType } from '../../Components/Table/DataTable/DataTable.model'
import { FormatDateValue } from '../../core/Utilities'
import { Typography } from '@mui/material'
import { RestraintEdit } from './RestraintEdit'
import { Dropdown } from '../../Components/Dropdown'

interface IncidentList {
  id: number
  serviceProviderName: string
  childFirstName: string
  childLastName: string
  reportedBy: string
  dateOfBirth: Date
  incidentDate: Date
  reportURL: string
}
export function RestraintList (): JSX.Element {
  const [incidents, setIncidents] = useState<IncidentList[]>([])
  const [loading, setLoading] = useState(true)
  const [editID, setEditID] = useState<number | null>(null)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [refresh, setRefresh] = useState(new Date())
  const reportURL = useRef('')

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response, success } = await sendGet('/RestraintEvent/GetList', {})
      if (success) {
        setIncidents(response)
        setLoading(false)
      } else {
        toast.error('Failed to load data')
      }
    }
    void loadData()
  }, [refresh])

  const rowClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): void => {
    setEditID(row.id)
    reportURL.current = row.reportURL
  }

  const printReport = (): void => {
    window.open(reportURL.current, '_blank')?.focus()
  }

  const closeEdit = (update: boolean): void => {
    setEditID(null)
    setShowEditDialog(false)
    if (update) {
      setRefresh(new Date())
    }
  }

  const ellipsisMenuColumn = new Column('ellipsisMenu', '', ColumnType.CUSTOM)
  ellipsisMenuColumn.customCellGenerator = row => {
    return <Dropdown
      selectComponent={<EllipsisIcon />}
      options={dropdownOptions}
      onOpenMenu={() => { setEditID(row.id) }}
    />
  }
  const dropdownOptions = [
    { name: 'Edit', onClick: (): void => { setShowEditDialog(true) } },
    { name: 'Print', onClick: printReport }
  ]

  const yearColumn = new Column('serviceProviderName', 'Service Provider')
  const childColumn = new Column('LastName', 'Child Name', ColumnType.CUSTOM)
  childColumn.customCellGenerator = row => {
    return <span>{row.childFirstName} {row.childLastName}</span>
  }
  const incidentDateColumn = new Column('incidentDate', 'Incident Date', ColumnType.CUSTOM)
  incidentDateColumn.customCellGenerator = row => {
    return <span>{FormatDateValue(row.incidentDate)} </span>
  }
  const reportedByColumn = new Column('reportedBy', 'Reported By')
  const columns = [ellipsisMenuColumn, yearColumn, childColumn, incidentDateColumn, reportedByColumn]

  const paginationProps: PaginationProps<RowData> = {
    rows: incidents
  }
  const pagination = usePagination(paginationProps)

  return <>
    <Typography variant="h3" data-testid='page-header'>Restraint Incidents</Typography >
    <Typography variant="body1" data-testid='page-subheader'>To enter an incident, search for the enrollment record and click Add Incident Event on the enrollment detail.</Typography>

    <DataTable
      page={pagination.page}
      data-testid="table"
      name='restraintTable'
      hasCheckbox={false}
      columns={columns}
      rows={pagination.internalRows ?? []}
      totalRecords={pagination.recordCount}
      loading={loading}
      onSortChange={(col, sorts) => {
        pagination.onSortChange(col, sorts[0]?.order)
      }}
      onPageChange={pagination.handleChangePage}
      onRowsPerPageChange={pagination.handleChangeRowsPerPage}
      onRowClick={rowClick}
    />

    {showEditDialog && editID !== null && <RestraintEdit editID={editID} enrollmentID={null} onClose={closeEdit} />}
  </>
}
